/**
 * V68: getWorkforceAllocationOverview callable hook (baseYearMonth ±2개월, 총 5개월)
 */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export interface MonthSlot {
  yearMonth: string;
  label: string;
}

export interface MonthEntry {
  // V68: BE 가 월별 LoA overlap 기준으로 'leave' entry 를 직접 생성. row.status 단일 값 의존 제거.
  type: 'idle' | 'project' | 'leave';
  label: string;
}

export interface MonthData {
  entries: MonthEntry[];
}

export interface WorkforceAllocationRow {
  userId: string;
  nameKo: string;
  orgUnitId: string;
  orgUnitName: string;
  companyCode: 'KR' | 'CN';
  employmentTypeCode: string;
  employmentTypeName: string;
  status: 'active' | 'on_leave';
  months: { [yearMonth: string]: MonthData };
}

export interface WorkforceScope {
  countries: ('KR' | 'CN')[];
  canChooseAll: boolean;
}

export interface AvailableOrg {
  orgUnitId: string;
  name: string;
  companyCode: 'KR' | 'CN';
}

export interface AvailableEmploymentType {
  code: string;
  name: string;
}

export interface AllocationResponse {
  success: boolean;
  baseYearMonth: string;
  months: MonthSlot[];
  employees: WorkforceAllocationRow[];
  scope: WorkforceScope;
  availableOrgs: AvailableOrg[];
  availableEmploymentTypes: AvailableEmploymentType[];
  error?: string;
}

export function useWorkforceAllocation(baseYearMonth?: string) {
  const [data, setData] = useState<WorkforceAllocationRow[]>([]);
  const [months, setMonths] = useState<MonthSlot[]>([]);
  const [resolvedBase, setResolvedBase] = useState<string | undefined>(baseYearMonth);
  const [scope, setScope] = useState<WorkforceScope>({ countries: [], canChooseAll: false });
  const [availableOrgs, setAvailableOrgs] = useState<AvailableOrg[]>([]);
  const [availableEmploymentTypes, setAvailableEmploymentTypes] = useState<AvailableEmploymentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<{ baseYearMonth?: string }, AllocationResponse>(
          getFunctions(), 'getWorkforceAllocationOverview'
        );
        const r = await fn({ baseYearMonth });
        if (cancelled) return;
        if (r.data.success) {
          setData(r.data.employees || []);
          setMonths(r.data.months || []);
          setResolvedBase(r.data.baseYearMonth);
          setScope(r.data.scope || { countries: [], canChooseAll: false });
          setAvailableOrgs(r.data.availableOrgs || []);
          setAvailableEmploymentTypes(r.data.availableEmploymentTypes || []);
        } else {
          setError(r.data.error || '조회 실패');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '서버 오류');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [baseYearMonth]);

  return { data, months, baseYearMonth: resolvedBase, scope, availableOrgs, availableEmploymentTypes, loading, error };
}
